import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Caption1, MessageBar, MessageBarBody, Spinner, Text } from '@fluentui/react-components';
import { SectionWorkspace, StatusDot, complianceTokenFor, tokens, type KeyValueItem, type WorkspaceSection } from '@vigeo/ui';
import { isComplianceTracked, isEmployed, personDisplayName } from '@vigeo/shared';
import { ReadinessBadge, statePresenters } from '@/features/readiness';
import { LifecycleControl, lifecyclePresenters, statusDetailLine } from '@/features/candidates';
import { fetchCatalogOptions, fetchChecklist, fetchPerson } from '@/lib/api';
import { fetchHrWorkflow, workflowHasOffer } from '@/features/hr-workflow';
import { HrWorkflowPanel } from '@/features/hr-workflow';
import type { OfferStageRequest } from '@/features/offer-stage';
import { fetchAuthState } from '@/lib/auth';
/*
 * EACH STEP'S CONTENT LIVES IN ITS OWN FILE (#462 §5). Steps 1, 2, 6 and 7 belong to the lead
 * team and 3, 4 and 5 to Carlos; with every one inline here, both teams edited the same file for
 * every change. This page keeps the ordering, the URL plumbing and the record header — the parts
 * that are nobody's step in particular.
 */
import {
  DocumentsStep, HistoryStep, OfferAndStageStep, OrientationStep, OverviewStep, PortalStep, ProfileStep, ReviewStep, candidateStepRail, recordLinkLabel, stepLabel, usePersonStyles,
} from '@/features/people';



export function PersonPage() {
  const styles = usePersonStyles();
  const { id = '' } = useParams();
  const [stageOpen, setStageOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const person = useQuery({ queryKey: ['person', id], queryFn: () => fetchPerson(id), enabled: id !== '' });
  const checklist = useQuery({ queryKey: ['checklist', id], queryFn: () => fetchChecklist(id), enabled: id !== '' });
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions });
  // Same key as the Offer and stage section, so the two share one read.
  const workflow = useQuery({ queryKey: ['hr-workflow', id], queryFn: () => fetchHrWorkflow(id), enabled: id !== '' });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  function sectionHref(section: string) {
    const search = new URLSearchParams(location.search); search.set('section', section);
    return `${location.pathname}?${search.toString()}`;
  }
  function selectSection(section: string) { void navigate(sectionHref(section)); }
  function draftOffer() {
    const search = new URLSearchParams(location.search); search.set('section', 'hiring'); search.set('draft', 'offer');
    void navigate(`${location.pathname}?${search.toString()}`);
  }
  // What the URL asks Offer and stage to open: the Overview's "Draft the offer", or a link from the
  // standalone Review and Send routes (`record` names the draft to edit).
  const offerRequest = ((): OfferStageRequest => {
    const search = new URLSearchParams(location.search);
    if (search.get('draft') === 'offer') return { kind: 'draft', recordId: search.get('record') };
    if (search.get('paper') === 'offer') return { kind: 'paper' };
    return null;
  })();
  // The request is spent once it has been acted on. Left in the URL it would ride along to every
  // other section and open a fresh editor on any reload.
  function offerRequestConsumed() {
    const search = new URLSearchParams(location.search);
    if (!search.has('draft') && !search.has('paper')) return;
    search.delete('draft'); search.delete('paper'); search.delete('record');
    void navigate(`${location.pathname}?${search.toString()}`, { replace: true });
  }

  if (person.isPending) return <div className={styles.page}><h1 className={styles.name}>Loading…</h1><Spinner size="small" label="Loading this person’s record" /></div>;
  if (person.isError) return <MessageBar intent="error"><MessageBarBody>No such person, or you do not have access to them.</MessageBarBody></MessageBar>;
  const record = person.data;
  const roles = record.jobProfileKeys.map((key) => options.data?.jobProfiles.find((profile) => profile.key === key)?.label ?? 'Role');
  const details: KeyValueItem[] = [
    { label: 'Role', value: roles.length > 0 ? roles.join(' · ') : 'Not set' },
    { label: 'Stage', value: [lifecyclePresenters[record.lifecycle].label, statusDetailLine(record.lifecycleDetail)].filter((part) => part !== null).join(' · ') },
    { label: 'Email', value: record.contact.email },
    ...(record.contact.phone === null ? [] : [{ label: 'Phone', value: record.contact.phone }]),
    ...(record.hireDate === null ? [] : [{ label: 'Hire date', value: new Date(record.hireDate).toLocaleDateString() }]),
    ...(record.employeeId === null ? [] : [{ label: 'Employee ID', value: record.employeeId }]),
  ];
  // Staff added by hand are not tracked yet (ADR-016 §4): say so, and do not ask HR to set a role. This one notice
  // is passed to the Overview and the Documents steps, so it covers both.
  const checklistNotice = !isComplianceTracked(record) ? <MessageBar intent="info"><MessageBarBody>
    Compliance is not tracked for this person yet. It starts when onboarding is connected.
  </MessageBarBody></MessageBar> : record.jobProfileKeys.length === 0 ? <MessageBar intent="warning"><MessageBarBody>
    <Text block>{statePresenters.setup_required.nextStep}</Text>
    No role set, so there is nothing to build a checklist from. Set a role to generate one.
    <Button appearance="subtle" onClick={() => selectSection('profile')}>Open profile</Button>
  </MessageBarBody></MessageBar> : checklist.isPending ? <Spinner size="small" label="Working out what this person needs" />
    : checklist.isError ? <MessageBar intent="error"><MessageBarBody>Could not build the checklist. Try again in a moment.</MessageBarBody></MessageBar> : null;
  const ready = checklistNotice === null ? checklist.data : undefined;
  // #428: a Candidate gets the short Overview; Onboarding and later keep the readiness panels.
  const isCandidate = record.lifecycle === 'candidate';
  // The frames address the person by name in these two section sentences.
  const firstName = record.name.preferredName || record.name.givenName;
  const hasOffer = workflow.data !== undefined && workflowHasOffer(workflow.data);

  // This single ordered definition drives both navigation modes, headings and next/previous.
  // Business progress still comes exclusively from the existing checklist and workflow data.
  const sections: WorkspaceSection[] = [
    { id: 'overview', label: stepLabel('overview'), description: isCandidate ? '' : 'Start with the current status and the next HR action.', href: sectionHref('overview'), content:
      <OverviewStep person={record} isCandidate={isCandidate} workflowPending={workflow.isPending} workflowError={workflow.isError}
        hasOffer={hasOffer} canEdit={canEdit} onDraftOffer={draftOffer} onOpenOffer={() => selectSection('hiring')}
        checklistNotice={checklistNotice} ready={ready} blockersHref={sectionHref('documents')} /> },
    { id: 'profile', label: stepLabel('profile'), description: 'Personal and contact information, employment details, roles and EMR access.', href: sectionHref('profile'), content:
      <ProfileStep person={record} canEdit={canEdit} details={details} openTriggerInputs={location.hash === '#trigger-inputs'} /> },
    { id: 'hiring', label: stepLabel('hiring'), description: isCandidate && workflow.data !== undefined && !hasOffer ? '' : 'Review offers, plan interviews and orientation, then update the person’s stage.', href: sectionHref('hiring'), content:
      <OfferAndStageStep person={record} canEdit={canEdit} roleLabel={roles.length > 0 ? roles.join(' · ') : null}
        onGoToDocuments={() => selectSection('documents')} initialRequest={offerRequest} onRequestConsumed={offerRequestConsumed} /> },
    /*
      STEP 7 HAS CONTENT NOW (#346). Both things here were rendering inside Offer and stage:
      scheduling an orientation is not part of making an offer, and the stage control is not a
      hiring control at all — `allowedTransitions` runs candidate→onboarding/withdrawn,
      onboarding→active/withdrawn, active→inactive/separated, inactive→active/separated. It is
      MOVED rather than dropped: deleting it would leave an active person with no way to become
      inactive or separated. Here it sits beside the orientation that has to happen first.
    */
    { id: 'orientation', label: stepLabel('orientation'), description: 'Schedule and record orientation.', href: sectionHref('orientation'), content:
      <OrientationStep person={record} canEdit={canEdit} /> },
    { id: 'portal', label: stepLabel('portal'), description: `${firstName}\u2019s secure link to the candidate portal. Field staff don\u2019t have accounts \u2014 the link and access code are how they get in.`, href: sectionHref('portal'), content:
      <PortalStep person={record} canEdit={canEdit} hasOffer={hasOffer} /> },
    { id: 'review', label: stepLabel('review'), description: `Everything ${firstName} has submitted through the portal. Approve or send back each item.`, href: sectionHref('review'), content:
      <ReviewStep person={record} canEdit={canEdit} /> },
    { id: 'documents', label: stepLabel('documents'), description: 'Work through requirements by group, open the forms sent to this person, and open stored files.', href: sectionHref('documents'), content:
      <DocumentsStep person={record} canEdit={canEdit} checklistNotice={checklistNotice} ready={ready} /> },
    { id: 'activity', label: recordLinkLabel('activity'), group: 'link' as const, description: 'Track assigned work, keep staff notes and review communications.', href: sectionHref('activity'), content: <HrWorkflowPanel person={record} canEdit={canEdit} view="activity" /> },
    { id: 'history', label: recordLinkLabel('history'), group: 'link' as const, description: 'Review the timeline, historical evidence, corrections and recorded decisions.', href: sectionHref('history'), content:
      <HistoryStep person={record} canEdit={canEdit} requirements={checklist.data?.items ?? []} ready={ready} /> },
    ...(canEdit ? [{ id: 'settings', label: recordLinkLabel('settings'), group: 'link' as const, description: 'Shared choices for future work across the agency: templates, milestones, competency mappings and screening schedules.', href: sectionHref('settings'), content: <HrWorkflowPanel person={record} canEdit={canEdit} view="settings" /> }] : []),
  ];

  /**
   * The rail the approved frames draw (1.2 294:567, 1.3 295:801, 2.2 322:1186): seven numbered
   * Candidate steps, then Follow-ups & messages, History and Agency settings as plain links.
   *
   * Portal and Review have no screen here yet (Orientation and start gained one with #346). They
   * are still listed,
   * because dropping them would renumber every step after them and the rail would stop matching
   * the design — and because the same three are already listed, unavailable, on the offer routes
   * (features/offers/OfferRecordFrame.tsx). Until this page and those routes agreed, the app
   * showed two different rails for the same person two clicks apart (#332).
   */
  /** The section with this id, or nothing — so an absent optional section cannot leave a hole. */
  const pick = (id: string) => sections.filter((section) => section.id === id);
  const PLACEHOLDER = (id: string, label: string): WorkspaceSection =>
    ({ id, label, description: '', href: sectionHref(id), content: null, available: false });
  const ordered: readonly WorkspaceSection[] = [
    // The order and the labels come from `candidateStepRail`, which the offer routes read too, so
    // the two rails cannot drift apart again (#462 §5).
    ...candidateStepRail.flatMap((step) => (step.built ? pick(step.id) : [PLACEHOLDER(step.id, step.label)])),
    ...sections.filter((section) => section.group === 'link'),
  ];
  const requested = location.hash === '#trigger-inputs' ? 'profile' : new URLSearchParams(location.search).get('section');
  const selectable = ordered.filter((section) => section.available !== false);
  const selectedId = selectable.find((section) => section.id === requested)?.id ?? selectable[0]!.id;

  /*
   * AN EMPLOYEE'S RECORD IS NOT A WALK THROUGH THE HIRING STAGES (#721). Active and inactive staff, reached
   * from the Roster, got the pipeline's wording: a "Candidate steps" rail, a "Candidate step" picker and
   * "Section 4 of 7". Julio: "these are roster employees, this is incorrect". The sections are the same,
   * in the same order, so nothing an employee's record could do is lost; they are named for what they
   * are and are not counted. The two literals on the call below are the candidate's, and stay.
   */
  const employeeRail = isEmployed(record.lifecycle) ? { label: 'Record sections', itemLabel: 'Record section', numbered: false } : {};

  return <SectionWorkspace key={record.id} label="Candidate steps" itemLabel="Candidate step" {...employeeRail} sections={ordered} selectedId={selectedId} onSelect={selectSection} header={
    <header className={styles.head}>
      <div className={styles.headline}><h1 className={styles.name}>{personDisplayName(record.name)}</h1>{/* A brand-new candidate is "not ready" by definition, so the badge says nothing for them —
          except Setup required, which is something HR has to fix (#428). */}
        {/* Staff added by hand are not tracked yet (ADR-016 §4): their checklist can only say "Setup required", which would read as a fault. */}
        {!isComplianceTracked(record) ? <Caption1>Not tracked yet</Caption1>
          : checklist.data && (!isCandidate || checklist.data.state === 'setup_required') ? <ReadinessBadge state={checklist.data.state} /> : null}</div>
      <div className={styles.meta}>
        {/*
          THE STAGE IS CHANGED FROM THE HEADER, ON EVERY STEP (Paul, 2026-09-29).
          It briefly lived in step 7 (#358) and he answered plainly: "Not in step 7. It's the stage
          badge in the record header, clickable on every step of the record." The header is rendered
          by `SectionWorkspace` above every section, so placing it here is what "every step" means.
          Before that it sat inside Offer and stage, which #346 is about: a control that runs the
          whole lifecycle, parked in the hiring section.
          A viewer sees the same badge as plain text — the control is the only part `canEdit` gates.
        */}
        {canEdit ? (
          <button
            type="button"
            className={styles.stageButton}
            aria-expanded={stageOpen}
            /*
             * NAMED, because a button whose only content is a coloured dot and a stage word says
             * nothing about what pressing it does. The visible text is the stage label, and WCAG
             * 2.5.3 requires the accessible name to contain it — so the label is prefixed, never
             * replaced.
             */
            aria-label={`Change stage: ${lifecyclePresenters[record.lifecycle].label}`}
            onClick={() => setStageOpen((open) => !open)}
          >
            <StatusDot color={record.lifecycle === 'onboarding' ? complianceTokenFor.good : tokens.colorNeutralForeground3}>{lifecyclePresenters[record.lifecycle].label}</StatusDot>
          </button>
        ) : (
          <StatusDot color={record.lifecycle === 'onboarding' ? complianceTokenFor.good : tokens.colorNeutralForeground3}>{lifecyclePresenters[record.lifecycle].label}</StatusDot>
        )}
        <Text>{roles.length ? roles.join(' · ') : 'No role set'}</Text>
      </div>
      {canEdit && stageOpen ? (
        <section className={styles.stagePanel} aria-label="Stage">
          <LifecycleControl person={record} canEdit={canEdit} onCancel={() => setStageOpen(false)} />
        </section>
      ) : null}
    </header>
  } />;
}
